// Shared geometries, materials, and tint helpers for the Infrastructure layer.
// Module-level singletons are mutated in place by the instanced-mesh effects
// during the commit phase — sequential execution means no concurrent-use risk.

import { playerColor } from '@aod/ui';
import * as THREE from 'three';

// ── Road / bridge ───────────────────────────────────────────────────────────
export const roadGeo = new THREE.PlaneGeometry(1.2, 1.2);
roadGeo.rotateX(-Math.PI / 2);

// Base road material — neutral tan. Per-owner tinting is applied via
// instanceColor, leaving this as the fallback for tiles without an owner.
export const roadMat = new THREE.MeshStandardMaterial({
  color: '#ffffff',
  roughness: 0.85,
  metalness: 0.0,
  depthWrite: true,
  transparent: true,
  opacity: 0.85,
});

export const bridgeMat = new THREE.MeshStandardMaterial({
  color: '#a08060',
  roughness: 0.7,
  metalness: 0.1,
  depthWrite: true,
  transparent: true,
  opacity: 0.9,
});

// Tints the white road base by blending 60% tan with 40% owner color so the
// road still reads as a road, not a colored stripe.
const ROAD_TAN = new THREE.Color('#8b7355');

export function roadTint(ownerId: number | undefined): THREE.Color {
  const owner = new THREE.Color(playerColor(ownerId));
  return ROAD_TAN.clone().lerp(owner, 0.4);
}

// ── Dominion-path materials (M5) ──────────────────────────────────────────
// Bridges and roads take on the controlling path's construction material
// (dominion-path-visuals.md §3): iron for Industry, luminous for Science,
// banner-hung gold for Faith, runestone for Arcane, living root for Nature,
// bone for Chaos. Default stays the plain wood/tan base.

type Archetype = 'industry' | 'science' | 'faith' | 'arcane' | 'nature' | 'chaos';

const PATH_MATERIAL: Record<Archetype, string> = {
  industry: '#62626a', // riveted iron
  science: '#a8dade', // luminous pale glass
  faith: '#c9a540', // banner-hung gold
  arcane: '#8a76b0', // runestone
  nature: '#7a8a44', // living root
  chaos: '#cfc4ac', // bone and chain
};

const BRIDGE_WOOD = new THREE.Color('#7a5a3a');

/** Dark-alignment tone: cooled, darker (spec §2.2). */
function toneColor(c: THREE.Color): THREE.Color {
  return c.multiply(new THREE.Color(0.5, 0.5, 0.55));
}

/**
 * Bridge material color for a path. No archetype → plain wood.
 */
export function bridgeTint(
  archetype: string | undefined,
  alignment: string | undefined,
): THREE.Color {
  if (!archetype || !(archetype in PATH_MATERIAL)) return BRIDGE_WOOD.clone();
  const c = new THREE.Color(PATH_MATERIAL[archetype as Archetype]);
  return alignment === 'dark' ? toneColor(c) : c;
}

/**
 * Road tint with the path woven in: owner colour as before, then mixed 35%
 * toward the controlling path's accent (the same mix the territory borders
 * use) and dark-toned for Dark owners.
 */
export function roadTintFor(
  ownerId: number | undefined,
  archetype: string | undefined,
  alignment: string | undefined,
): THREE.Color {
  const base = roadTint(ownerId);
  if (!archetype || !(archetype in PATH_MATERIAL)) {
    return alignment === 'dark' ? toneColor(base) : base;
  }
  const accent = new THREE.Color(PATH_MATERIAL[archetype as Archetype]);
  const mixed = base.lerp(accent, 0.35);
  return alignment === 'dark' ? toneColor(mixed) : mixed;
}

// ── Depot ───────────────────────────────────────────────────────────────────
// Depot marker: a small supply-crate box on a slightly wider base plate.
// Tier scales the crate height so upgraded depots read as more substantial.
export const depotBaseGeo = new THREE.BoxGeometry(0.5, 0.04, 0.5);
export const depotCrateGeo = new THREE.BoxGeometry(0.32, 0.24, 0.32);
depotCrateGeo.translate(0, 0.14, 0);

export const depotMat = new THREE.MeshStandardMaterial({
  color: '#ffffff',
  roughness: 0.6,
  metalness: 0.05,
  depthWrite: true,
});

// Stronger tint for depots — they should read as owned structures, not terrain.
export function depotTint(ownerId: number | undefined): THREE.Color {
  const owner = new THREE.Color(playerColor(ownerId));
  return owner.clone().lerp(new THREE.Color('#443322'), 0.2);
}

// ── Ruin ────────────────────────────────────────────────────────────────────
// Ruin marker: a tapered obelisk on a wide base. Visually distinct from
// depots so players can tell "ancient site" from "supply chain".
export const ruinBaseGeo = new THREE.CylinderGeometry(0.22, 0.28, 0.06, 6);
export const ruinShaftGeo = new THREE.CylinderGeometry(0.08, 0.14, 0.5, 6);
ruinShaftGeo.translate(0, 0.31, 0);

export const ruinMat = new THREE.MeshStandardMaterial({
  color: '#ffffff',
  roughness: 0.9,
  metalness: 0.0,
  depthWrite: true,
});

// Uncollected ruins glow faintly gold (treasure waiting); collected turn to granite gray.
const RUIN_GOLD = new THREE.Color('#caa84a');
const RUIN_GRAY = new THREE.Color('#6e635a');

export function ruinTint(collected: boolean): THREE.Color {
  return collected ? RUIN_GRAY.clone() : RUIN_GOLD.clone();
}

// ── Shared scratch singletons (mutated in place during instanced updates) ──
export const _dummy = new THREE.Object3D();
export const _color = new THREE.Color();

// Shared entry shape — a tile plus its original index within the tiles array.
export interface InfraEntry {
  readonly tile: import('@aod/protocol').SceneNode;
  readonly idx: number;
}
